import { Box, Button, Typography, styled } from "@mui/material";
import { sumBy } from "lodash";
import uniq from "lodash-es/uniq";
import { ReactElement, useEffect, useState } from "react";

import {
  OverlapSuppressionFilterType,
  SuppressionStage,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import InfoToolip from "~frontend/components/InfoTooltip";
import Table from "~frontend/components/Table";
import TooltipButton from "~frontend/components/TooltipButton";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import { palette } from "~frontend/theme";

import useTagName from "../../campaignEditorUtils/useTagName";
import filterLabels from "../filterLabels";
import { isSendInProgress } from "../sendInProgress";
import { Suppression } from "./types";

const Wrapper = styled(Box)({
  backgroundColor: "white",
});

export default function SuppressionsTable({
  campaign,
  suppressions,
  stage,
  onRemove,
  onUpload,
  readOnly,
}: {
  campaign: CampaignWithSuppressionData;
  suppressions: Suppression[];
  stage: SuppressionStage;
  onRemove: (selections: {
    overlapIds: string[];
    listIds: string[];
    shortcodeSubscriberSuppressionIds: string[];
  }) => Promise<void>;
  onUpload: () => void;
  readOnly: boolean;
}): ReactElement {
  const [removeLoading, setRemoveLoading] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  async function onClickRemove() {
    setRemoveLoading(true);

    try {
      await onRemove({
        overlapIds: campaign.campaignOverlaps
          .filter(o => selectedRowIds.includes(o.id))
          .map(o => o.id),
        listIds: campaign.suppressionLists
          .filter(l => selectedRowIds.includes(l.id))
          .map(l => l.id),
        shortcodeSubscriberSuppressionIds:
          campaign.shortcodeSubscriberSuppressions
            .filter(s => selectedRowIds.includes(s.id))
            .map(s => s.id),
      });
      setSelectedRowIds([]);
    } finally {
      setRemoveLoading(false);
    }
  }

  useEffect(() => {
    // remove any selected rows that no longer exist
    const validSuppressionIds = new Set(suppressions.map(o => o.id));
    const validSelectedIds = uniq(
      selectedRowIds.filter(overlapId => validSuppressionIds.has(overlapId))
    );

    if (validSelectedIds.length !== selectedRowIds.length) {
      setSelectedRowIds(validSelectedIds);
    }
  }, [selectedRowIds, suppressions]);

  const sendInProgress = isSendInProgress(campaign);

  const { getTagName, state: tagNameState } = useTagName();

  return (
    <Wrapper>
      {suppressions.length > 0 ? (
        <>
          <Table
            noFooter
            columns={[
              {
                key: "source",
                title: "Source",
                renderValue: row => (
                  <>
                    {row.name}
                    {row.kind === "overlap" && row.filter ? (
                      <>
                        <br />
                        <Typography display="inline" variant="body3">
                          Filter:{" "}
                          {row.filter === OverlapSuppressionFilterType.Tagged
                            ? `Contacts who were tagged with ${row.filterTagIds.map(id => (tagNameState === "ready" ? (getTagName(id) ?? "(deleted tag)") : "(loading)")).join(" or ")}`
                            : filterLabels[row.filter]}
                        </Typography>
                      </>
                    ) : null}
                  </>
                ),
              },
              {
                key: "createdAt",
                title: "Added By",
                align: "left",
                renderValue: row => (
                  <>
                    {row.appliedByFullname}
                    <br />
                    <Typography variant="body3">
                      {row.appliedAt.toLocaleString()}
                    </Typography>
                  </>
                ),
              },
              {
                key: "count",
                title: "Overlaps",
                align: "right",
                renderValue: row => row.count.toLocaleString(),
              },
            ]}
            rows={suppressions}
            selectedRows={readOnly ? undefined : selectedRowIds}
            onChangeSelectedRows={readOnly ? undefined : setSelectedRowIds}
          />
          <FlexBox
            sx={{ pt: 2, textAlign: "right" }}
            justifyContent="flex-end"
            alignItems="center"
          >
            {stage === SuppressionStage.PostStart ? (
              <>
                <Typography>
                  Total Post-Launch Suppressions:{" "}
                  <strong>
                    {campaign.suppressedPostStartContactCount.toLocaleString()}
                  </strong>
                </Typography>
                {campaign.suppressedPostStartContactCount !==
                sumBy(suppressions, "count") ? (
                  <InfoToolip>
                    The total count of suppressions may be less than the sum of
                    the suppressions from different lists if lists contain
                    overlapping contacts.
                  </InfoToolip>
                ) : null}
              </>
            ) : (
              <Typography>
                Total Pre-Launch Suppressions:{" "}
                <strong>{sumBy(suppressions, "count").toLocaleString()}</strong>
              </Typography>
            )}
          </FlexBox>
        </>
      ) : (
        <FlexBox justifyContent="center" sx={{ pt: 5, pb: 2, px: 3 }}>
          <Typography
            variant="body2"
            textAlign="center"
            sx={{ color: palette.darkGray }}
          >
            No suppressions added. Your suppressions will appear here once
            you've added them from previous campaigns or uploaded a CSV.
          </Typography>
        </FlexBox>
      )}

      {!readOnly && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: "24px",
          }}
        >
          {suppressions.length > 0 ? (
            <Button
              variant="contained"
              color="accessory"
              onClick={onClickRemove}
              disabled={removeLoading || !selectedRowIds.length}
            >
              Remove Selected
            </Button>
          ) : (
            <div />
          )}
          <TooltipButton
            variant="contained"
            color="accessory"
            sx={{
              color: palette.white,
              backgroundColor: palette.navy,
              "&:hover": { backgroundColor: palette.darkBlue },
            }}
            onClick={onUpload}
            tooltipText={
              sendInProgress
                ? "Cannot suppress contacts while sending is in progress"
                : undefined
            }
            disabled={sendInProgress}
          >
            Upload A CSV
          </TooltipButton>
        </Box>
      )}
    </Wrapper>
  );
}
